import { Auth0Provider as _Auth0Provider } from '@auth0/auth0-react';
import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { useHotkeys } from '@mantine/hooks';
import { ModalsProvider as MantineModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConnectedRouter } from 'connected-react-router';
import { Provider as RawJotaiProvider } from 'jotai';
import { DevTools as JotaiDevTools } from 'jotai-devtools';
import { useTranslation } from 'locales';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { useLocation } from 'react-router';
import { isDefined, isNonNullish, isNullish } from 'remeda';
import apiService from 'shared/service/api.service';
import { isProduction, isPublicEnvironment } from 'shared/utils/environment';
import { config } from 'store/constants';
import { rootSagas } from 'store/root.sagas';
import { loginLandingPath } from './auth0-redirect';
import { jotaiStore } from './jotai';
import { mantineTheme } from './mantine';
import classes from './Providers.module.scss';
import { buildPosthogIdentity } from './posthog-identity';
import { queryClientV5 } from './query-client';
import { browserHistory, sagaMiddleware, store } from './redux-store';
import { useEnforceProfile } from './use-enforce-profile';

const DevToolsWrapper = () => {
  const [areDevtoolsVisible, setAreDevtoolsVisible] = useState(false);
  useHotkeys([['shift+mod+D', () => setAreDevtoolsVisible(state => !state)]]);

  if (isPublicEnvironment()) return null;

  if (!areDevtoolsVisible) return null;

  return (
    <span className={classes.devtools}>
      <ReactQueryDevtools initialIsOpen={false} />
      {/* @ts-ignore */}
      <JotaiDevTools store={jotaiStore} />
    </span>
  );
};

export const Providers = ({ children }: React.PropsWithChildren<{}>) => {
  sagaMiddleware.run(rootSagas);

  return (
    <Auth0Provider>
      <ReduxProviders store={store} history={browserHistory}>
        <JotaiProvider>
          <ReactQueryProviders>
            <PostHogProvider>
              <MantineProviders>
                {children}
                <DevToolsWrapper />
                <Notifications zIndex={1000} position="bottom-right" />
              </MantineProviders>
            </PostHogProvider>
          </ReactQueryProviders>
        </JotaiProvider>
      </ReduxProviders>
    </Auth0Provider>
  );
};

const Auth0Provider = ({ children }: React.PropsWithChildren<{}>) => (
  // eslint-disable-next-line react/jsx-pascal-case
  <_Auth0Provider
    domain={config.auth0.domain}
    clientId={config.auth0.clientId}
    cacheLocation="localstorage"
    useRefreshTokens
    onRedirectCallback={appState => browserHistory.replace(loginLandingPath(appState))}
    authorizationParams={{
      audience: config.auth0.audience,
      redirect_uri: `${window.location.origin}/dashboard`,
      scope: 'openid profile email offline_access',
    }}
  >
    {children}
  </_Auth0Provider>
);

const ReduxProviders = ({ store: _store, history, children }: any) => (
  <ReduxProvider store={_store}>
    {/* @ts-ignore react 19 */}
    <ConnectedRouter history={history}>{children}</ConnectedRouter>
  </ReduxProvider>
);

const JotaiProvider = ({ children }: React.PropsWithChildren<{}>) => (
  <RawJotaiProvider store={jotaiStore}>{children}</RawJotaiProvider>
);

const ReactQueryProviders = ({ children }: React.PropsWithChildren<{}>) => (
  <QueryClientProvider client={queryClientV5}>{children}</QueryClientProvider>
);

const MantineProviders = ({ children }: React.PropsWithChildren<{}>) => {
  const { currentLang } = useTranslation();

  return (
    <MantineProvider theme={mantineTheme}>
      <DatesProvider settings={{ locale: currentLang }}>
        <MantineModalsProvider>{children}</MantineModalsProvider>
      </DatesProvider>
    </MantineProvider>
  );
};

const PostHogProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const location = useLocation();
  const { profile } = useEnforceProfile();

  useEffect(() => {
    if (isProduction() && isDefined(profile)) {
      posthog.init('phc_f3vuQblHJAnPKJA0qKZan0PmalMguSQJdPFSEw0Upsc', {
        api_host: 'https://eu.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: false,
      });

      const identity = buildPosthogIdentity({ profile, tenant: apiService.tenant });
      posthog.identify(identity.distinctId, identity.personProperties);
      posthog.group(identity.group.type, identity.group.key, identity.group.properties);
    }
  }, [isNullish(profile)]);

  useEffect(() => {
    if (isNonNullish(posthog)) {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
      });
    }
  }, [location]);

  return <PHProvider client={posthog}>{children}</PHProvider>;
};
